import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faShareNodes } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import QRCode from 'react-qr-code'
import { speakToKitSerifStyle } from '../theme/speakToKitThemeVars'

type ShareInviteCardProps = {
    shareInviteUrl: string
    whatsAppShareUrl: string
    isMobileShareAvailable: boolean
    isDesktopViewport: boolean
    hasCopiedLink: boolean
    onCopy: () => void | Promise<void>
    onShare: () => void | Promise<void>
    title?: string
    body?: string
}

const defaultTitle = 'Thanks for speaking with Kit!'
const defaultBody = 'Share your Kit link with someone who should try it.'
const SHARE_URL_DISPLAY_LIMIT = 42

export default function ShareInviteCard({
    shareInviteUrl,
    whatsAppShareUrl,
    isMobileShareAvailable,
    isDesktopViewport,
    hasCopiedLink,
    onCopy,
    onShare,
    title = defaultTitle,
    body = defaultBody,
}: ShareInviteCardProps): React.JSX.Element {
    const truncatedShareInviteUrl =
        shareInviteUrl.length > SHARE_URL_DISPLAY_LIMIT
            ? `${shareInviteUrl.slice(0, SHARE_URL_DISPLAY_LIMIT)}...`
            : shareInviteUrl

    return (
        <div className="w-full rounded-3xl border border-border bg-card px-4 py-5 shadow-[var(--shadow-card)] sm:p-6 md:p-8">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_auto] lg:items-start">
                <div className="flex flex-col gap-5">
                    <div className="space-y-2 text-center sm:text-left">
                        <h2
                            className="text-2xl font-semibold text-foreground md:text-[28px]"
                            style={speakToKitSerifStyle}
                        >
                            {title}
                        </h2>
                        {body?.trim() ? (
                            <p className="text-sm text-muted-foreground md:text-base">
                                {body}
                            </p>
                        ) : null}
                    </div>
                    <div className="rounded-2xl border border-border bg-secondary/40 md:p-4 p-2 shadow-[var(--shadow-soft)]">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <a
                                href={shareInviteUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="min-w-0 flex-1 rounded-xl border border-border bg-white px-4 py-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-primary"
                                title={shareInviteUrl}
                            >
                                <span className="block truncate">
                                    {truncatedShareInviteUrl}
                                </span>
                            </a>
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                <button
                                    type="button"
                                    onClick={onCopy}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                    Copy link
                                </button>
                                {isMobileShareAvailable ? (
                                    <button
                                        type="button"
                                        onClick={onShare}
                                        className="w-full sm:w-auto sm:hidden inline-flex items-center justify-center gap-2 rounded-full border border-primary/40 px-6 py-3 text-base font-semibold text-primary transition-all hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2"
                                    >
                                        <FontAwesomeIcon icon={faShareNodes} />
                                        Share
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        {hasCopiedLink ? (
                            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                                Link copied
                            </div>
                        ) : null}
                    </div>
                </div>
                {isDesktopViewport ? (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-background/70 p-4 text-center">
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            <FontAwesomeIcon
                                icon={faWhatsapp}
                                className="h-4 w-4 text-[#25D366]"
                            />
                            <span>WhatsApp</span>
                        </div>
                        <div className="inline-flex rounded-2xl bg-white p-3 shadow-sm">
                            <QRCode
                                value={whatsAppShareUrl}
                                size={132}
                                bgColor="#ffffff"
                                fgColor="#111827"
                            />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Scan to share
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    )
}
